import { computed, type ComputedRef, type MaybeRef, toValue } from 'vue'
import { addDays, intervalToDuration, isValid, parseISO, startOfDay } from 'date-fns'
import { deepGet, isObject } from 'ts-deep-utils'
import { Mask } from 'maska'
import { getPropertyInfo } from '../composables/useSwagger'
import { isMediaFileReference, type Address } from '../types/_general'
import i18n from '../plugins/i18n'
import Personnummer from 'personnummer'
import Organisationsnummer from 'organisationsnummer'
import Kontonummer from 'kontonummer'
import type { ReferenceObject, SchemaObject } from 'openapi-typescript'

export function getIntlLocales(): string[] {
  return [i18n.global.locale.value, 'sv-SE']
}

/** Schema/config formats whose values benefit from `tabular-nums` digit
 *  alignment. Includes both numeric magnitudes and fixed-width dates. */
export const TABULAR_FORMATS: ReadonlySet<string> = new Set([
  'date',
  'date-time',
  'deadline',
  'number',
  'currency',
  'slider',
])

/** Subset of {@link TABULAR_FORMATS} that should be right-aligned in tables.
 *  Numeric magnitudes benefit from right-alignment for comparison; dates have
 *  fixed-width formatting (`YYYY-MM-DD …`) so they stay at the default
 *  `start` alignment. */
export const NUMERIC_FORMATS: ReadonlySet<string> = new Set(['number', 'currency', 'slider'])

/** Subset of {@link TABULAR_FORMATS} that renders date-like values. Table
 *  cells use this to prevent line breaks inside a date (`text-no-wrap`). */
export const DATE_FORMATS: ReadonlySet<string> = new Set(['date', 'date-time', 'deadline'])

/**
 * Format a number
 * @param value The number to format
 * @returns The formatted number string from Intl.NumberFormat - 100 000
 */
export function formatNumber(value: number | undefined): string {
  if (value == null) return ''
  return new Intl.NumberFormat(getIntlLocales(), { style: 'decimal' }).format(value)
}

/**
 * Format a number as a percentage
 * @param value The number to format (e.g., 0.25 for 25%, or 25 for 25%)
 * @param options.alreadyPercent If true, the value is already a percentage (e.g., 25 instead of 0.25)
 * @returns The formatted percentage string from Intl.NumberFormat - for example 25%
 */
export function formatPercentage(value: number | undefined, options?: { alreadyPercent?: boolean }): string {
  if (value == null) return ''
  const normalizedValue = options?.alreadyPercent === true ? value / 100 : value
  return new Intl.NumberFormat(getIntlLocales(), { style: 'percent' }).format(normalizedValue)
}

/**
 * Format a number as a currency
 * @param value The number to format
 * @param currency The currency to use (default: SEK)
 * @param fractionDigits Optional number of fraction digits. If undefined, uses currency default (typically 2).
 * Use 0 for whole numbers, 2 for standard decimal display.
 * @returns The formatted currency string from Intl.NumberFormat - for example 100 kr
 */
export function formatCurrency(value: number | undefined, currency = 'SEK', fractionDigits?: number): string {
  if (value == null) return ''

  const options: Intl.NumberFormatOptions = { style: 'currency', currency }

  if (fractionDigits != null) {
    options.minimumFractionDigits = fractionDigits
    options.maximumFractionDigits = fractionDigits
  }

  return new Intl.NumberFormat(getIntlLocales(), options).format(value)
}

/**
 * Format a date as a short date string
 * @param value The date to format
 * @returns The formatted date string from Intl.DateTimeFormat - for example 2025-11-27
 */
export function formatDate(value: string | number | Date): string {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (value == null || !isValid(new Date(value))) return ''
  return new Intl.DateTimeFormat(getIntlLocales(), { dateStyle: 'short' }).format(new Date(value))
}

/**
 * Format a date as a date and time string
 * @param value The date to format
 * @returns The formatted date string from Intl.DateTimeFormat - for example 2025-11-27 10:02
 */
export function formatDateTime(value: string | number | Date): string {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (value == null || !isValid(new Date(value))) return ''
  return new Intl.DateTimeFormat(getIntlLocales(), { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

/**
 * Format a deadline date using calendar-day boundaries, with relative-text
 * for ±1 day around today.
 *
 * - "expires" prefix:   yesterday uses "Förföll …" (past tense), today and
 *   future use "Förfaller …".
 * - "plain" prefix:     bare relative text ("Igår" / "Idag" / "Imorgon"),
 *   or a plain date outside the ±1 window.
 * - "scheduled" prefix: like plain but hides past dates entirely (a scheduled
 *   time in the past means the flow has stalled — showing it would mislead).
 */
export function formatDeadlineDate(
  value: string | number | Date | null | undefined,
  options?: { prefix?: 'expires' | 'plain' | 'scheduled'; now?: Date }
): string {
  if (value == null) return ''
  // Strings may arrive as ISO 8601 (`2026-05-05` / `2026-05-05T12:00:00Z`) or
  // as the RFC 1123 form the API uses (`Tue, 14 Jan 2026 13:20:03 GMT`).
  // `parseISO` only accepts ISO; fall back to `new Date(...)` for RFC 1123.
  let target: Date
  if (typeof value === 'string') {
    target = parseISO(value)
    if (!isValid(target)) target = new Date(value)
  } else {
    target = new Date(value)
  }
  if (!isValid(target)) return ''

  const prefix = options?.prefix ?? 'expires'
  const now = options?.now ?? new Date()

  // Day boundaries via date-fns so DST transitions don't shift them by an hour.
  const startOfToday = startOfDay(now)
  const startOfYesterday = addDays(startOfToday, -1)
  const startOfTomorrow = addDays(startOfToday, 1)
  const startOfDayAfterTomorrow = addDays(startOfToday, 2)

  const t = i18n.global.t

  if (prefix === 'scheduled' && target < startOfToday) return ''

  if (target < startOfYesterday) {
    return prefix === 'expires' ? t('common.expired_on', { date: formatDate(target) }) : formatDate(target)
  }
  if (target < startOfToday) {
    return prefix === 'expires' ? t('common.expired_yesterday') : t('common.yesterday')
  }
  if (target < startOfTomorrow) {
    return prefix === 'expires' ? t('common.expires_today') : t('common.today')
  }
  if (target < startOfDayAfterTomorrow) {
    return prefix === 'expires' ? t('common.expires_tomorrow') : t('common.tomorrow')
  }

  return prefix === 'expires' ? t('common.expires_on', { date: formatDate(target) }) : formatDate(target)
}

/**
 * Format a list of strings as a list string
 * @param value The list of strings to format
 * @param type The type of list to format (default: 'conjunction')
 * @returns The formatted list string from Intl.ListFormat - for example a, b, and c
 */
export function formatList(value: string[], type?: Intl.ListFormatOptions['type']): string {
  return new Intl.ListFormat(getIntlLocales(), { type }).format(value)
}

export type IntervalUnit = 'years' | 'months' | 'days' | 'hours' | 'minutes' | 'seconds'

const intervalUnits: { key: IntervalUnit; singular: string; plural: string }[] = [
  { key: 'years', singular: 'common.year', plural: 'common.years' },
  { key: 'months', singular: 'common.month', plural: 'common.months' },
  { key: 'days', singular: 'common.day', plural: 'common.days' },
  { key: 'hours', singular: 'common.hour', plural: 'common.hours' },
  { key: 'minutes', singular: 'common.minute', plural: 'common.minutes' },
  { key: 'seconds', singular: 'common.second', plural: 'common.seconds' },
]

interface FormatDateIntervalOptions {
  /** Start date for the interval (defaults to now) */
  startDate?: Date | string
  /** Specific unit to display (defaults to largest non-zero unit) */
  unit?: IntervalUnit
}

/**
 * Format a duration between two dates as human-readable text
 * @param endDate The target/end date
 * @param options Optional configuration for start date and unit
 * @returns Human-readable interval like "3 months" or "1 year", null if date is null/undefined or end is before start
 */
export function formatDateInterval(
  endDate: Date | string | null | undefined,
  options?: FormatDateIntervalOptions
): string | null {
  if (endDate == null) return null

  const start = options?.startDate != null ? new Date(options.startDate) : new Date()
  const end = new Date(endDate)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null
  if (end <= start) return null

  const duration = intervalToDuration({ start, end })

  if (options?.unit != null) {
    const value = duration[options.unit] ?? 0
    const unitConfig = intervalUnits.find((u) => u.key === options.unit)
    if (unitConfig == null) return null
    const localeKey = value === 1 ? unitConfig.singular : unitConfig.plural
    const label = i18n.global.t(localeKey).toLocaleLowerCase(getIntlLocales())
    return `${value} ${label}`
  }

  for (const unit of intervalUnits) {
    const value = duration[unit.key] ?? 0
    if (value > 0) {
      const localeKey = value === 1 ? unit.singular : unit.plural
      const label = i18n.global.t(localeKey).toLocaleLowerCase(getIntlLocales())
      return `${value} ${label}`
    }
  }

  return null
}

/**
 * Format bytes as human-readable text.
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024. Defaults to true
 * @param dp Number of decimal places to display. Defaults to 1
 *
 * @return Formatted string - for example 4.1 MB
 */
export function formatByteSize(bytes: number, si = true, dp = 1) {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return `${bytes.toFixed(dp)} ${units[u]}`
}

/**
 * Truncate a string to a certain length by removing characters from the middle,
 * inserting an ellipsis in its place
 * @param val The string to truncate
 * @param length The length of the string to truncate to
 * @returns The truncated string, for example Hello World could become He…ld
 */
export function truncateCenter(val: string, length: number) {
  if (val.length < length) return val
  const lettersToTake = Math.round((length - 1) / 2)
  return `${val.slice(0, lettersToTake)}…${val.slice(-lettersToTake)}`
}

/**
 * Format an ID number (personal number or organisation number) in the proper display format
 * @param idNumber The ID number to format
 * @returns The formatted ID number, for example 200001011234 could become 000101-1234
 */
export function formatIdNumber(idNumber: string) {
  if (Personnummer.valid(idNumber)) {
    return Personnummer.parse(idNumber).format()
  } else if (Organisationsnummer.valid(idNumber)) {
    return Organisationsnummer.parse(idNumber).format()
  } else {
    return idNumber
  }
}

/**
 * Format a string with proper case (title case with hyphen support)
 * @param value The string to format
 * @returns The formatted string with:
 *   - First letter of each word capitalized
 *   - First letter after hyphens capitalized
 *   - All other letters lowercased
 * @example formatProperCase("JOHN DOE") => "John Doe"
 * @example formatProperCase("anne-marie smith") => "Anne-Marie Smith"
 */
export function formatProperCase(value: string): string {
  return value
    .toLocaleLowerCase(getIntlLocales())
    .replace(
      /(^|[\s-])(\S)/g,
      (_: string, prefix: string, char: string) => prefix + char.toLocaleUpperCase(getIntlLocales())
    )
}

/**
 * Render a list value on a single line for table cells and value rows.
 * Handles both flat lists (`["a", "b"]` → `"a, b"`) and lists of objects
 * (`[{ creditor_tag: id, creditor_tag_name: "Storkund" }, …]` →
 * `"Storkund, Sjukvård"`). For object elements the display value is a string
 * subfield: an explicit `displayKey` when given, otherwise the heuristic
 * prefers a `*_name` key, then `name`, then the first string property. Pass
 * `displayKey` when a list carries several `*_name`-suffixed keys (the heuristic
 * then depends on key order). Elements with no displayable value are dropped.
 *
 * When `maxItems` is given and there are more entries than that, the surplus is
 * collapsed into a `+N` suffix (e.g. `"Storkund, Sjukvård +2"`) — used for
 * compact table cells; the untruncated string (no `maxItems`) is kept for
 * tooltips and detail views.
 */
export function formatListValue(list: unknown[], maxItems?: number, displayKey?: string): string {
  const entries = list
    .map((element) => {
      if (element == null) return ''
      if (typeof element === 'string') return element
      if (typeof element === 'number' || typeof element === 'boolean' || typeof element === 'bigint') {
        return String(element)
      }
      if (typeof element !== 'object') return '' // symbol / function — not displayable
      const record = element as Record<string, unknown>
      const keys = Object.keys(record)
      // Resolve the display key sequentially: explicit override → *_name → name
      // → first string property.
      let resolvedKey = displayKey
      resolvedKey ??= keys.find((key) => key.endsWith('_name'))
      resolvedKey ??= keys.find((key) => key === 'name')
      resolvedKey ??= keys.find((key) => typeof record[key] === 'string')
      return resolvedKey != null ? String(record[resolvedKey]) : ''
    })
    .filter((entry) => entry !== '')

  if (maxItems != null && entries.length > maxItems) {
    return `${formatList(entries.slice(0, maxItems), 'unit')} +${entries.length - maxItems}`
  }
  return formatList(entries, 'unit')
}

/** An IBAN in the conventional groups-of-four display form (country-agnostic; the
 *  final group may be shorter). */
export function formatIban(iban: string): string {
  return iban
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

/** Swedish clearing + account number rendered as one line via the `kontonummer`
 *  library; falls back to "<clearing> <account>" when it can't validate/parse them. */
export function formatBankAccount(clearing: string, account: string): string {
  try {
    return new Kontonummer(clearing, account, { mode: 'strict' }).format('pretty')
  } catch {
    // Expected for accounts the library can't validate/parse (e.g. unusual/foreign).
    return `${clearing} ${account}`.trim()
  }
}

/** The address fields {@link formatAddress} renders; any other key (`gln`, `bounce`, …) is ignored. */
type FormatAddressInput = Partial<
  Pick<Address, 'address_line_1' | 'address_line_2' | 'address_line_3' | 'zip_code' | 'city' | 'country'>
>

/** The translated country name for an ISO 3166-1 alpha-2 code, or the code itself when no `country.<code>` text exists. */
function formatCountry(code: string | undefined): string | undefined {
  if (code == null || code === '') return undefined
  const key = `country.${code.toLocaleLowerCase(getIntlLocales())}`
  const translated = i18n.global.t(key)
  return translated === key ? code : translated
}

/**
 * Format an address as one line: address lines 1–3, zip code and city, and the translated country name
 * @param address The address to format; any subset of the fields may be present
 * @returns The formatted address - for example Storgatan 1, 111 22 Stockholm, Sverige
 */
export function formatAddress(address: FormatAddressInput | null | undefined): string {
  if (address == null) return ''
  const zipCity = [address.zip_code, address.city].filter((part) => part != null && part !== '').join(' ')
  return [
    address.address_line_1,
    address.address_line_2,
    address.address_line_3,
    zipCity,
    formatCountry(address.country),
  ]
    .filter((part) => part != null && part !== '')
    .join(', ')
}

export function formatFromSchema(
  itemOrValue: unknown,
  propertyPath: string,
  resourceSchema: MaybeRef<SchemaObject | ReferenceObject | undefined>
): ComputedRef<string> {
  return computed(() => {
    const info = getPropertyInfo(toValue(resourceSchema), propertyPath)
    const probablyFileValue =
      info.format === 'file' && (itemOrValue instanceof File || isMediaFileReference(itemOrValue))
    const probablyItem = isObject(itemOrValue) || (info.format === 'file' && !probablyFileValue)
    const value = probablyItem ? deepGet(itemOrValue as Record<string, unknown>, propertyPath) : itemOrValue

    switch (info.format) {
      case 'number':
        return formatNumber(value as number)
      case 'number-as-string':
        return value == null ? '' : `${value as number | string}`
      case 'currency': {
        const currency =
          info.currencyPath && probablyItem
            ? (deepGet(itemOrValue as Record<string, unknown>, info.currencyPath) as string)
            : undefined
        return formatCurrency(value as number, currency ?? info.currency)
      }
      case 'date-time':
        return formatDateTime(value as string)
      case 'date':
        return formatDate(value as string)
      case 'deadline':
        return formatDeadlineDate(value as string | number | Date | null | undefined, {
          prefix: info.deadlinePrefix ?? 'expires',
        })
      case 'id-number':
        return formatIdNumber(value as string)
      case 'iban':
        return typeof value === 'string' ? formatIban(value) : ''
      case 'password':
        return '*'.repeat(`${value as string | number}`.length)
      case 'file': {
        let normalizedFile
        if (value instanceof File) {
          normalizedFile = value
        } else if (isMediaFileReference(value)) {
          normalizedFile = {
            name: value.filename ?? '',
            size: value.size ?? 0,
          }
        }
        return normalizedFile != null
          ? `${truncateCenter(normalizedFile.name, 16)} (${formatByteSize(normalizedFile.size)})`
          : (value as string)
      }
      case 'boolean':
        return value ? 'common.yes' : 'common.no'
      default:
        if (value == null) {
          return ''
        }
        if (Array.isArray(value)) {
          return formatListValue(value)
        }
        // An `id_number` dict is type-keyed: the number lives under the key named
        // by `id_number_type`. Render the populated value as an id-number so a
        // header/subtitle/subheader can reference the whole `id_number` attribute.
        if (isObject(value) && typeof (value as { id_number_type?: unknown }).id_number_type === 'string') {
          const idType = (value as { id_number_type: string }).id_number_type
          const raw = (value as Record<string, unknown>)[idType]
          return typeof raw === 'string' ? formatIdNumber(raw) : ''
        }
        // A `bank_account_number` dict (Swedish clearing + account) renders as one
        // formatted line, so payout displays are consistent everywhere they appear.
        if (isObject(value) && 'clearing_number' in value && 'account_number' in value) {
          const bankAccount = value as { clearing_number?: unknown; account_number?: unknown }
          const clearing = typeof bankAccount.clearing_number === 'string' ? bankAccount.clearing_number : ''
          const account = typeof bankAccount.account_number === 'string' ? bankAccount.account_number : ''
          return formatBankAccount(clearing, account)
        }
        if (typeof value === 'string') {
          if (info.masks != null && info.masks.length > 0) {
            return new Mask({ mask: info.masks }).masked(value)
          }
          return value
        }
        if (typeof value === 'boolean') {
          return value ? 'common.yes' : 'common.no'
        }
        if (typeof value === 'number') {
          return String(value)
        }
        console.warn('formatFromSchema: unexpected value type', value)
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        return String(value)
    }
  })
}
